import { useBrand } from "@/contexts/BrandContext";
import { useAuth } from "@/contexts/AuthContext";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import ArmadaSwitcher from "@/components/ArmadaSwitcher";

export default function Header() {
    const brand = useBrand();
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const search = location.search; // preserve white-label query string

    const navItems = [
        { to: "/", label: "Home", end: true },
        { to: "/about", label: "About" },
        { to: "/vision", label: "Vision" },
        { to: "/star", label: "Star" },
        { to: "/foundry/interview", label: "Interview" },
        { to: "/exchange/post", label: "Post a Mission" },
    ];
    if (user && typeof user === "object") {
        navItems.push({ to: "/exchange", label: "Exchange" });
        navItems.push({ to: "/dashboard", label: "Dashboard" });
    }

    const linkClass = ({ isActive }) =>
        `caption transition-colors hover:text-[#FAFAFA] ${isActive ? "text-[var(--brand-accent)]" : "text-[#A3A3A3]"}`;

    return (
        <header
            data-testid="white-label-header"
            className="sticky top-0 z-40 w-full border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur-2xl"
        >
            <div className="mx-auto flex h-20 max-w-[1400px] items-center justify-between px-6 md:px-10">
                <Link
                    to={{ pathname: "/", search }}
                    data-testid="brand-logo"
                    className="flex items-center gap-3"
                >
                    {brand.logo ? (
                        <img
                            src={brand.logo}
                            alt={brand.name}
                            className="h-9 max-w-[160px] object-contain"
                        />
                    ) : (
                        <div
                            className="flex h-9 w-9 items-center justify-center rounded-sm border"
                            style={{
                                borderColor: "var(--brand-accent)",
                                color: "var(--brand-accent)",
                            }}
                        >
                            <span className="font-serif text-xl leading-none">S</span>
                        </div>
                    )}
                    <div className="hidden sm:block leading-tight">
                        <div className="font-serif text-2xl tracking-display text-[#FAFAFA]">
                            {brand.name}
                        </div>
                        <div className="caption text-[#737373]">{brand.tagline}</div>
                    </div>
                </Link>

                <nav className="hidden items-center gap-8 md:flex">
                    {navItems.map((n) => (
                        <NavLink
                            key={n.to}
                            to={{ pathname: n.to, search }}
                            end={n.end}
                            className={linkClass}
                            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {n.label}
                        </NavLink>
                    ))}
                    <ArmadaSwitcher />
                </nav>

                <div className="flex items-center gap-3">
                    {user && typeof user === "object" ? (
                        <>
                            <span className="hidden text-sm text-[#A3A3A3] md:inline">
                                {user.name || user.email}
                            </span>
                            <button
                                data-testid="btn-logout"
                                onClick={async () => {
                                    await logout();
                                    navigate(`/${search}`);
                                }}
                                className="inline-flex items-center justify-center rounded-sm px-4 py-2 text-sm font-medium text-[#FAFAFA] transition-all duration-300 hover:bg-white/5"
                            >
                                Sign out
                            </button>
                        </>
                    ) : (
                        <>
                            <Link
                                data-testid="nav-login"
                                to={{ pathname: "/login", search }}
                                className="caption text-[#A3A3A3] hover:text-[#FAFAFA]"
                            >
                                Sign in
                            </Link>
                            <Link
                                data-testid="nav-register"
                                to={{ pathname: "/register", search }}
                                className="inline-flex items-center justify-center rounded-sm px-5 py-2.5 text-sm font-medium transition-all duration-300"
                                style={{
                                    background: "var(--brand-accent)",
                                    color: "#0A0A0A",
                                }}
                            >
                                Become a Recruiter
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}
